import { Construction, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";

export default function Dashboard() {
  const navigate = useNavigate();

  return (
    <div className="flex flex-col items-center justify-center min-h-[80vh] px-4 text-center">
      <div className="bg-amber-100 p-6 rounded-full mb-6">
        <Construction className="h-16 w-16 text-amber-600" />
      </div>
      
      <h1 className="text-3xl font-bold text-foreground mb-3">
        Estamos construindo seu Dashboard!
      </h1>
      
      <p className="text-muted-foreground text-lg max-w-md mb-8">
        Em breve você terá acesso a métricas, gráficos e insights 
        completos do seu negócio.
      </p>
      
      <Button onClick={() => navigate('/')} variant="outline">
        <Home className="mr-2 h-4 w-4" />
        Voltar para Home
      </Button>
    </div>
  );
}

/* 
===========================================
CÓDIGO ORIGINAL DO DASHBOARD (COMENTADO)
Para restaurar, descomente este código e delete o código acima
===========================================

import React, { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import { Users, Briefcase, CheckCircle, AlertCircle, Shield, Calendar as CalendarIcon, ChevronLeft, Loader2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { format, subDays, startOfMonth, endOfMonth, startOfYear, subMonths } from 'date-fns';
import { cn } from '@/lib/utils';
import { supabase } from "@/integrations/supabase/client";
import { getLocalDayStart, getLocalDayEnd, getLocalWeekStart, getLocalMonthStart, getDaysAgo } from '@/lib/timezoneUtils';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@/components/ui/select";

import StatCard from "../components/dashboard/StatCard";
import CallsMetrics from "../components/dashboard/CallsMetrics";
import SalesMetrics from "../components/dashboard/SalesMetrics";
import PipelineMetrics from "../components/dashboard/PipelineMetrics";
import { TabGroup } from "@/components/common/TabGroup";
import { useUserRole } from "@/hooks/useUserRole";
import { useAuth } from "@/hooks/useAuth";

const timeRanges = [
  { label: 'Hoje', value: 'today' },
  { label: 'Ontem', value: 'yesterday' },
  { label: 'Últimos 7 dias', value: 'last7Days' },
  { label: 'Este mês', value: 'thisMonth' },
  { label: 'Mês passado', value: 'lastMonth' },
  { label: 'Este ano', value: 'thisYear' },
  { label: 'Personalizado', value: 'custom' },
];

const formatDate = (date) => {
  return format(date, 'dd/MM/yyyy');
};

export default function Dashboard() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [date, setDate] = useState({ from: subDays(new Date(), 7), to: new Date() });
  const [open, setOpen] = useState(false);
  const [selectedTimeRange, setSelectedTimeRange] = useState(timeRanges[2].value);
  const [isLoading, setIsLoading] = useState(false);
	const [isFirstLoading, setIsFirstLoading] = useState(true);
  const [stats, setStats] = useState({
    totalUsers: 0,
    activeUsers: 0,
    totalCompanies: 0,
    activeCompanies: 0,
    totalCalls: 0,
    avgCallDuration: 0,
    totalSales: 0,
    avgSaleValue: 0,
    wonOpportunities: 0,
    lostOpportunities: 0,
    openOpportunities: 0,
  });
  const [userRole, isLoadingRole] = useUserRole();

  const isAdmin = userRole === 'admin';

  useEffect(() => {
    if (!isLoadingRole) {
      fetchStats();
    }
  }, [isLoadingRole, selectedTimeRange, date]);

  const fetchStats = async () => {
    setIsLoading(true);

    let fromDate = date?.from;
    let toDate = date?.to;

    if (selectedTimeRange === 'today') {
      fromDate = getLocalDayStart(new Date());
      toDate = getLocalDayEnd(new Date());
    } else if (selectedTimeRange === 'yesterday') {
      const yesterday = subDays(new Date(), 1);
      fromDate = getLocalDayStart(yesterday);
      toDate = getLocalDayEnd(yesterday);
    } else if (selectedTimeRange === 'last7Days') {
      fromDate = getDaysAgo(7);
      toDate = getLocalDayEnd(new Date());
    } else if (selectedTimeRange === 'thisMonth') {
      fromDate = getLocalMonthStart(new Date());
      toDate = getLocalDayEnd(new Date());
    } else if (selectedTimeRange === 'lastMonth') {
      const lastMonth = subMonths(new Date(), 1);
      fromDate = getLocalMonthStart(lastMonth);
      toDate = endOfMonth(lastMonth);
    } else if (selectedTimeRange === 'thisYear') {
      fromDate = startOfYear(new Date());
      toDate = getLocalDayEnd(new Date());
    }

    try {
      const { data: statsData, error: statsError } = await supabase.functions.invoke('get-dashboard-stats', {
        body: {
          from: fromDate,
          to: toDate,
          userId: user?.id,
          isAdmin: isAdmin,
        },
      });

      if (statsError) {
        console.error("Erro ao buscar estatísticas:", statsError);
        toast.error(t('dashboard.fetchStatsError'));
        return;
      }

      setStats(statsData);
    } catch (error) {
      console.error("Erro ao invocar a função:", error);
      toast.error(t('dashboard.invokeFunctionError'));
    } finally {
			setIsFirstLoading(false);
      setIsLoading(false);
    }
  };

  const handleTimeRangeChange = (value) => {
    setSelectedTimeRange(value);
  };

  return (
    <div className="container relative pt-6">
      <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold">{t('dashboard.title')}</h1>
        <div className="flex items-center space-x-2">
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                id="date"
                variant={"outline"}
                className={cn(
                  "w-[280px] justify-start text-left font-normal",
                  !date && "text-muted-foreground"
                )}
              >
                <CalendarIcon className="mr-2 h-4 w-4" />
                {date?.from ? (
                  formatDate(date.from) + " - " + formatDate(date.to)
                ) : (
                  <span>{t('dashboard.pickDate')}</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0" align="end">
              <Calendar
                mode="range"
                defaultMonth={date?.from}
                selected={date}
                onSelect={setDate}
                numberOfMonths={2}
                pagedNavigation
              />
            </PopoverContent>
          </Popover>

          <Select value={selectedTimeRange} onValueChange={handleTimeRangeChange}>
            <SelectTrigger className="w-[180px]">
              <span>
                {timeRanges.find((range) => range.value === selectedTimeRange)?.label}
              </span>
            </SelectTrigger>
            <SelectContent>
              {timeRanges.map((range) => (
                <SelectItem key={range.value} value={range.value}>
                  {range.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

			{isFirstLoading ? (
				<div className="flex items-center justify-center h-48">
					<Loader2 className="mr-2 h-6 w-6 animate-spin" />
					<span>{t('dashboard.loadingStats')}</span>
				</div>
			) : (
				<>
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
						<StatCard
							title={t('dashboard.totalUsers')}
							value={stats.totalUsers}
							icon={Users}
							isLoading={isLoading}
						/>
						<StatCard
							title={t('dashboard.activeUsers')}
							value={stats.activeUsers}
							icon={CheckCircle}
							isLoading={isLoading}
						/>
						<StatCard
							title={t('dashboard.totalCompanies')}
							value={stats.totalCompanies}
							icon={Briefcase}
							isLoading={isLoading}
						/>
						<StatCard
							title={t('dashboard.activeCompanies')}
							value={stats.activeCompanies}
							icon={Shield}
							isLoading={isLoading}
						/>
					</div>

					<TabGroup
						tabs={[
							{ label: t('dashboard.callsMetrics'), content: <CallsMetrics stats={stats} isLoading={isLoading} t={t} /> },
							{ label: t('dashboard.salesMetrics'), content: <SalesMetrics stats={stats} isLoading={isLoading} t={t} /> },
							{ label: t('dashboard.pipelineMetrics'), content: <PipelineMetrics stats={stats} isLoading={isLoading} t={t} /> },
						]}
					/>
				</>
			)}
    </div>
  );
}

*/
